import type { HugeNumber } from "@/shared/numbers";

export const EXPLORATION_BIOME_IDS = ["hov", "tim", "mat", "lev"] as const;

export type ExplorationBiomeId = (typeof EXPLORATION_BIOME_IDS)[number];

export const EXPLORATION_MP_ALLOCATION_AMOUNTS = [1, 3, 6] as const;

export type ExplorationMpAllocationAmount =
  (typeof EXPLORATION_MP_ALLOCATION_AMOUNTS)[number];

export type ExplorationMpSlotId =
  `${ExplorationBiomeId}-${ExplorationMpAllocationAmount}`;

export interface ExplorationAreaState {
  readonly progressMs: number;
}

export interface ExplorationState {
  readonly biomeLevel: number;
  readonly purchasedMpSlotCount: number;
  readonly cumulativeTimAddedMs: number;
  readonly timAccelerationEnabled: boolean;
  readonly areas: Readonly<Record<ExplorationBiomeId, ExplorationAreaState>>;
}

export type ExplorationEffect =
  | { readonly type: "award-ohov-power"; readonly amount: number }
  | { readonly type: "add-exploration-time"; readonly milliseconds: number }
  | { readonly type: "multiply-hg"; readonly multiplier: HugeNumber }
  | { readonly type: "raise-biome-level"; readonly amount: number };

export interface ExplorationAreaDefinition {
  readonly id: ExplorationBiomeId;
  readonly periodMs: number;
  readonly resolveEffect: (biomeLevel: number) => ExplorationEffect;
}

export interface ExplorationMpSlotDefinition {
  readonly id: ExplorationMpSlotId;
  readonly biomeId: ExplorationBiomeId;
  readonly amount: ExplorationMpAllocationAmount;
}

export interface ExplorationTimAccelerationDefinition {
  readonly unlockCumulativeTimAddedMs: number;
  readonly timPerGameSecond: number;
  readonly speedMultiplier: number;
}

export interface ExplorationDefinition {
  readonly tabUnlockCost: HugeNumber;
  readonly areas: Readonly<
    Record<ExplorationBiomeId, ExplorationAreaDefinition>
  >;
  readonly mpSlots: readonly ExplorationMpSlotDefinition[];
  readonly mpSlotCosts: readonly (HugeNumber | null)[];
  readonly timAcceleration: ExplorationTimAccelerationDefinition;
}

export type ExplorationCommandBlockedReason =
  | "exploration-locked"
  | "price-unconfigured"
  | "insufficient-hov-core"
  | "all-mp-slots-unlocked"
  | "invalid-mp-slot"
  | "mp-slot-locked"
  | "mpm-section-disabled"
  | "already-assigned"
  | "global-mp-capacity-exceeded"
  | "not-assigned";

export interface ExplorationMpSummary {
  readonly used: number;
  readonly total: number;
  readonly available: number;
}

export interface ExplorationProgressionEffects {
  readonly completionCounts: Readonly<Record<ExplorationBiomeId, number>>;
  readonly ohovPowerAwarded: number;
  readonly timAddedMs: number;
  readonly hgMultiplier: HugeNumber;
  readonly biomeLevelsGained: number;
}

export interface ExplorationAreaSnapshot extends ExplorationAreaState {
  readonly id: ExplorationBiomeId;
  readonly periodMs: number;
  readonly remainingMs: number;
  readonly assignedMp: number;
  readonly pointerParticipation: number;
  readonly participation: number;
}

export interface ExplorationPurchasedMpSlotSnapshot extends ExplorationMpSlotDefinition {
  readonly assigned: boolean;
}

export interface ExplorationMpSlotDetailsSnapshot extends ExplorationPurchasedMpSlotSnapshot {
  readonly pausedByMpm: boolean;
  readonly hiddenByMpm: boolean;
  readonly assignAvailability: ExplorationOperationAvailability;
  readonly removeAvailability: ExplorationOperationAvailability;
}

export interface ExplorationAreaDetailsSnapshot extends ExplorationAreaSnapshot {
  readonly isRunning: boolean;
  readonly currentEffect: ExplorationEffect;
  readonly mpSlots: readonly ExplorationMpSlotDetailsSnapshot[];
}

export interface ExplorationMpSlotPurchaseSnapshot {
  readonly slot: ExplorationMpSlotDefinition | null;
  readonly cost: HugeNumber | null;
  readonly availability: ExplorationOperationAvailability;
}

export type ExplorationTimAccelerationStatus =
  | "locked"
  | "stopped"
  | "running"
  | "insufficient-tim";

export interface ExplorationTimAccelerationSnapshot {
  readonly unlocked: boolean;
  readonly enabled: boolean;
  readonly isApplied: boolean;
  readonly status: ExplorationTimAccelerationStatus;
  readonly timPerGameSecond: number;
  readonly speedMultiplier: number;
  readonly currentTimPerGameSecond: number;
  readonly currentSpeedMultiplier: number;
}

export interface ExplorationSnapshot {
  readonly biomeLevel: number;
  readonly purchasedMpSlotCount: number;
  readonly cumulativeTimAddedMs: number;
  readonly purchasedMpSlots: readonly ExplorationPurchasedMpSlotSnapshot[];
  readonly areas: Readonly<Record<ExplorationBiomeId, ExplorationAreaSnapshot>>;
  readonly mp: ExplorationMpSummary;
}

export interface ExplorationDetailsSnapshot extends ExplorationSnapshot {
  readonly biomeIds: readonly ExplorationBiomeId[];
  readonly areas: Readonly<
    Record<ExplorationBiomeId, ExplorationAreaDetailsSnapshot>
  >;
  readonly nextMpSlotPurchase: ExplorationMpSlotPurchaseSnapshot;
  readonly timAcceleration: ExplorationTimAccelerationSnapshot;
}

export type ExplorationOperationAvailability =
  | { readonly canExecute: true; readonly cost?: HugeNumber }
  | {
      readonly canExecute: false;
      readonly reason: ExplorationCommandBlockedReason;
    };

export type ExplorationModelCommandResult =
  | { readonly ok: true; readonly snapshot: ExplorationDetailsSnapshot }
  | {
      readonly ok: false;
      readonly reason: ExplorationCommandBlockedReason;
      readonly snapshot: ExplorationDetailsSnapshot;
    };
